import {
  LobbyButton,
  LeaveEventButton,
  PresentButton,
  ScreenSharingButton,
  MicrophoneButton,
  CameraButton,
} from "modules/eventButtons";
import { useMediaQuery } from "@remo-co/ui-core/src/hooks/useMediaQuery";
import { PresentContentButton } from "modules/eventEmbedContent";
import { HelpChatButton } from "modules/helpChat";
import { RecordingControls } from "modules/recording";
import EmojiButtonContainer from "modules/emoji/EmojiButtonContainer/EmojiButtonContainer";
import { ExpandableContainer } from "modules/ExpandableContainer";
import useI18n from "i18n/useI18n";
import { useSmallDesktop } from "modules/common/hooks/useSmallDesktop";
import { ClosedCaptionButton } from "modules/transcription";
import classNames from "classnames";
import { ChatBubble } from "@remo-co/ui-core/src/icons/ChatBubble";
import { DevicePromptPopup } from "modules/audioVideo";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { Divider } from "@remo-co/ui-core/src/components/Divider";
import { lightTheme } from "@remo-co/ui-core/src/theme/lightTheme";
import AudienceViewIcon from "../AudienceViewIcon";
import { useStyles } from "./styles";
import MoreMenu from "../MoreMenu";
import { RightPanelTabButton } from "../RightPanelTabButton";

interface Props {
  isEventManager: boolean;
}

const ManagerButtons = ({ isEventManager }: Props): JSX.Element => {
  const { t } = useI18n(["common"]);
  const styles = useStyles();
  const isDesktop = useMediaQuery(lightTheme.breakpoints.up("sm"));

  const isSmallDesktop = useSmallDesktop();

  return (
    <ExpandableContainer
      components={[
        {
          id: "help-chat",
          component: (
            <Box marginLeft={lightTheme.spacing(0.1)}>
              <HelpChatButton
                color="dark"
                darkMode={false}
                variant="ghost"
                className={classNames(styles.helpChatButton)}
              />
            </Box>
          ),
          layout: "hide-on-mobile",
        },
        {
          id: "recording-control",
          component: isEventManager && (
            <Box marginLeft={lightTheme.spacing(0.1)}>
              <RecordingControls />
            </Box>
          ),
          layout: "desktop-only",
        },
        {
          id: "audience",
          className: styles.push,
          component: <AudienceViewIcon />,
          layout: "hide-on-mobile",
        },
        {
          id: "present",
          component: isEventManager && <PresentButton darkMode />,
          layout: "desktop-only",
        },
        {
          id: "divider-1",
          component: isEventManager && (
            <Divider
              className={classNames(styles.divider)}
              orientation="vertical"
            />
          ),
          layout: "desktop-only",
        },
        {
          id: "microphone",
          component: <MicrophoneButton darkMode />,
          layout: "desktop-and-mobile",
        },
        {
          id: "camera",
          component: <CameraButton darkMode />,
          layout: "desktop-and-mobile",
        },
        {
          id: "device-prompt-overlay",
          component: <DevicePromptPopup />,
          layout: "desktop-and-mobile",
        },
        {
          id: "screensharing",
          component: <ScreenSharingButton darkMode />,
          layout: "desktop-only",
        },
        {
          id: "present-content",
          component: <PresentContentButton />,
          layout: "hide-on-mobile",
        },
        {
          id: "divider-2",
          component: (
            <Divider
              className={`${styles.divider} ${styles.hideOnMobile}`}
              orientation="vertical"
            />
          ),
          layout: "desktop-only",
        },
        {
          id: "chat",
          component: (
            <RightPanelTabButton
              text={t("common:chat.button")}
              icon={<ChatBubble />}
            />
          ),
          layout: "desktop-and-mobile",
        },
        {
          id: "lobby",
          component: isEventManager && <LobbyButton darkMode />,
          layout: "hide-on-mobile",
        },
        {
          id: "closed-caption-toggle",
          component: <ClosedCaptionButton darkMode />,
          layout: "hide-on-mobile",
        },
        {
          id: "leave-event",
          component: !isEventManager && <LeaveEventButton darkMode />,
          layout: "hide-on-mobile",
        },
        {
          id: "more-menu",
          className: classNames(styles.push),
          component: isDesktop && <MoreMenu isEventManager={isEventManager} />,
          layout: "hide-on-mobile",
        },
        {
          id: "emoji",
          component: !isSmallDesktop && (
            <Box className={styles.hideOnMobile}>
              <EmojiButtonContainer />
            </Box>
          ),
          layout: "desktop-only",
        },
      ]}
      desktopContainerClassName={styles.container}
      mobileContainerClassName={styles.mobileContainer}
    />
  );
};

export default ManagerButtons;
